import React, { useState } from 'react';
import { Modal, Button, Form, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { writeContract, waitForTransactionReceipt } from '@wagmi/core';
import communitiesContractABI from '../../contracts/tgetherCommunities.json';
import { useNavigate } from 'react-router-dom';
import * as config from '../../config.tsx';

const communitiesContractAddress = process.env.REACT_APP_COMMUNITIES_CONTRACT_ADDRESS as `0x${string}`;

interface VoteModalProps {
  show: boolean;
  onHide: () => void;
  proposalId: string;
}

const VoteModal: React.FC<VoteModalProps> = ({ show, onHide, proposalId }) => {
  const [voteChoice, setVoteChoice] = useState(true); // true for approve, false for deny
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleVote = async () => {
    setLoading(true);
    setError(''); // Reset any existing error messages

    try {
      // Submit the vote using the `vote` method in the contract
      const tx = await writeContract(config.config, {
        abi: communitiesContractABI.abi,
        address: communitiesContractAddress,
        functionName: 'vote',
        args: [proposalId, voteChoice], // Passing the proposalId and voteChoice (approve/deny)
      });

      // Wait for the transaction receipt
      await waitForTransactionReceipt(config.config, {
        hash: tx,
      });

      setLoading(false);
      onHide(); // Close the modal after voting

      // Navigate back to the proposal page to refresh
      navigate(`/search-results?type=Proposal&value=${proposalId}`);
    } catch (error: any) {
      console.error('Error casting vote:', error);
      if (error.message.includes('revert')) {
        setError('Transaction failed: Smart contract execution reverted. Please check your inputs.');
      } else if (error.message.includes('out of gas')) {
        setError('Transaction failed: Out of gas. Try increasing the gas limit.');
      } else {
        setError('Failed to submit your vote. Please try again.');
      }
      setLoading(false);
    }
  };

  const renderTooltip = (text: string) => (
    <Tooltip id="button-tooltip">
      {text}
    </Tooltip>
  );

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Vote on Proposal {proposalId}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form>
          <Form.Group>
            <Form.Label>
              Vote Choice{' '}
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip('Choose to approve or deny the proposal.')}>
                <Button
                  variant="secondary"
                  className="ml-1 p-0"
                  style={{ border: 'none', background: 'none' }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Check
              type="radio"
              label="Approve"
              name="voteChoice"
              id="approve"
              checked={voteChoice === true}
              onChange={() => setVoteChoice(true)}
            />
            <Form.Check
              type="radio"
              label="Deny"
              name="voteChoice"
              id="deny"
              checked={voteChoice === false}
              onChange={() => setVoteChoice(false)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleVote} disabled={loading}>
          {loading ? 'Submitting...' : 'Submit Vote'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VoteModal;
